import axios from "axios";
import { Dispatch } from "react";
import { UserStateInterface } from "../../../middleware/interfaces/user/user-state-interface";
import { FileUploadInterface } from "../../../middleware/interfaces/file/file-upload-interface";
import { ModelUploadInterface } from "../../../middleware/interfaces/file/model-upload-interface";
import { UploadTargetInterface } from "../../../middleware/interfaces/file/upload-target-interface";

/**
 * Uploads run in three steps: ask the server for presigned URLs, PUT the bytes
 * straight to Wasabi, then tell the server the objects landed.
 *
 * The file never passes through digitalcharcoal.io. Cloudflare rejects any
 * proxied request body over 100 MB, and the old flow base64 encoded the file
 * into a JSON body, so the real ceiling was closer to 75 MB.
 */

const IssueUploadUrls = async (
  uploadData: FileUploadInterface | ModelUploadInterface
) => {
  const url = "./php/file/file-upload-issuer-proxy.php";

  // Strip the payload down to metadata. fileData and modelData hold a File and
  // an ArrayBuffer, which have no meaningful JSON form and must not be sent.
  const { fileData, modelData, ...uploadMetaData } = uploadData as any;

  const response = await axios.post(url, uploadMetaData);
  return response.data;
};

const PutToStorage = async (
  uploads: UploadTargetInterface[],
  uploadBodies: { [key: string]: Blob | ArrayBuffer },
  uploadSizes: { [key: string]: number },
  dispatch: Dispatch<any>
) => {
  // A bare client, not the shared axios default. Wasabi signs only the host
  // header, and anything an interceptor adds later -- an Authorization header
  // in particular -- would be rejected alongside the query-string signature.
  const storageAxios = axios.create();

  const totalBytes = Object.values(uploadSizes).reduce(
    (total, size) => total + size,
    0
  );
  const loadedBytes: { [key: string]: number } = {};

  for (const upload of uploads) {
    await storageAxios.put(upload.url, uploadBodies[upload.target], {
      headers: {
        "Content-Type": "application/octet-stream",
      },
      onUploadProgress: (progressEvent) => {
        loadedBytes[upload.target] = progressEvent.loaded;

        const uploadedBytes = Object.values(loadedBytes).reduce(
          (total, loaded) => total + loaded,
          0
        );

        // Held below 100 so the dialog does not claim completion while the
        // server is still confirming the objects.
        dispatch({
          type: "UPLOAD_PROGRESS",
          payload: Math.min(
            99,
            Math.round((uploadedBytes / totalBytes) * 100)
          ),
        });
      },
    });
  }
};

const CompleteUpload = async (
  uploadData: FileUploadInterface | ModelUploadInterface,
  issuedUpload: any
) => {
  const url = "./php/file/file-upload-completer-proxy.php";

  const response = await axios.post(url, {
    email: uploadData.email,
    projectID: uploadData.projectID,
    type: uploadData.type,
    fileID: issuedUpload.fileID,
    fileVersionNumber: issuedUpload.fileVersionNumber,
  });

  return response.data;
};

export const FileUpload = {
  Upload: async (state: UserStateInterface, dispatch: Dispatch<any>) => {
    let serverResponse;

    if (state.data) {
      const uploadData: FileUploadInterface | ModelUploadInterface = state.data;

      let uploadBodies: { [key: string]: Blob | ArrayBuffer };
      let uploadSizes: { [key: string]: number };

      if (uploadData.type === "FileUploadInterface") {
        uploadBodies = { file: uploadData.fileData };
        uploadSizes = { file: uploadData.fileSize };
      } else {
        uploadBodies = {
          file: uploadData.modelData.modelFileData,
          fragments: uploadData.modelData.modelFragmentsData,
        };
        uploadSizes = {
          file: uploadData.modelFileSize,
          fragments: uploadData.modelFragmentsSize,
        };
      }

      try {
        dispatch({ type: "UPLOAD_PROGRESS", payload: 0 });

        const issueResponse = await IssueUploadUrls(uploadData);

        // The issuer answers with "upload" only once it has reserved the rows.
        // Anything else is a permission, quota or validation refusal that the
        // caller should surface unchanged.
        if (issueResponse.header !== "upload") {
          return issueResponse;
        }

        await PutToStorage(
          issueResponse.data.uploads,
          uploadBodies,
          uploadSizes,
          dispatch
        );

        serverResponse = await CompleteUpload(uploadData, issueResponse.data);
      } catch (error: any) {
        serverResponse = {
          status: "error",
          header: "error",
          message: error.message,
        };
      } finally {
        dispatch({ type: "UPLOAD_PROGRESS", payload: null });
      }
    } else {
      serverResponse = {
        status: "error",
        header: "error",
        message: "Something went wrong! Please contact DigitalCharcoal",
      };
    }

    return serverResponse;
  },
};
